import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    RefreshCw,
    Search,
    Check,
    XCircle,
    Trash2,
    Plus,
    Flag,
    ChevronRight,
} from 'lucide-react';
import { AdminConfirmDialog } from '@/components/shared/AdminConfirmDialog';
import {
    useAdminFishingClubsStats,
    useAdminFishingClubsList,
    useAdminFishingClubSetStatus,
    useAdminFishingClubDelete,
    useAdminFishingClubCreate,
    useAdminFishingClubReportsList,
    useAdminFishingClubReportSetStatus,
} from './hooks/useFishingClubsAdmin';

type ClubTab = 'pending' | 'approved' | 'rejected' | 'all';

export function ClubsPage() {
    const [mainTab, setMainTab] = useState<'clubs' | 'reports'>('clubs');
    const [clubTab, setClubTab] = useState<ClubTab>('pending');
    const [search, setSearch] = useState('');
    const [rejectId, setRejectId] = useState<string | null>(null);
    const [rejectReason, setRejectReason] = useState('');
    const [createOpen, setCreateOpen] = useState(false);
    const [reportTab, setReportTab] = useState<'open' | 'all'>('open');
    const [deleteClubId, setDeleteClubId] = useState<string | null>(null);

    const [create, setCreate] = useState({
        creator_user_id: '',
        name: '',
        description: '',
        external_url: 'https://',
        country_code: 'ES',
        location_label: 'España',
        initials: '',
    });

    const statusFilter = clubTab === 'all' ? 'all' : clubTab;
    const { data: stats } = useAdminFishingClubsStats();
    const { data: rows = [], isLoading, refetch } = useAdminFishingClubsList(
        statusFilter === 'all' ? '' : statusFilter,
        search
    );
    const setStatus = useAdminFishingClubSetStatus();
    const del = useAdminFishingClubDelete();
    const createMut = useAdminFishingClubCreate();
    const { data: reports = [], refetch: refetchReports } = useAdminFishingClubReportsList(
        reportTab === 'open' ? 'open' : ''
    );
    const reportSet = useAdminFishingClubReportSetStatus();

    const tabs = useMemo(
        () => [
            { value: 'all' as const, label: 'Todos', count: stats?.total },
            { value: 'pending' as const, label: 'Pendientes', count: stats?.pending_count },
            { value: 'approved' as const, label: 'Aprobados', count: stats?.approved_count },
            { value: 'rejected' as const, label: 'Rechazados', count: stats?.rejected_count },
        ],
        [stats]
    );

    const handleCreate = () => {
        if (!create.creator_user_id.trim() || !create.name.trim() || !create.initials.trim()) return;
        createMut.mutate(
            {
                p_creator_user_id: create.creator_user_id.trim(),
                p_name: create.name.trim(),
                p_description: create.description.trim() || 'Club',
                p_external_url: create.external_url.trim(),
                p_logo_url: null,
                p_country_code: create.country_code.trim(),
                p_location_label: create.location_label.trim(),
                p_latitude: 40.4168,
                p_longitude: -3.7038,
                p_visibility: 'public',
                p_water_types: ['dulce'],
                p_modalities: ['spinning'],
                p_location_slugs: ['orilla'],
                p_initials: create.initials.trim().toUpperCase().slice(0, 5),
                p_verified: true,
                p_auto_add_creator_member: true,
            },
            {
                onSuccess: () => {
                    setCreateOpen(false);
                    setCreate((c) => ({ ...c, name: '', description: '', initials: '' }));
                },
            }
        );
    };

    return (
        <div className="space-y-6">
            <PageHeader title="Clubes de pesca" description="Detalle, miembros, aprobación y denuncias" />

            <Tabs value={mainTab} onValueChange={(v) => setMainTab(v as 'clubs' | 'reports')}>
                <TabsList>
                    <TabsTrigger value="clubs">Clubes</TabsTrigger>
                    <TabsTrigger value="reports">
                        Denuncias {stats?.reports_open ? `(${stats.reports_open})` : ''}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="clubs" className="space-y-4 mt-4">
                    <div className="flex flex-wrap gap-2 items-center justify-between">
                        <div className="flex gap-2 flex-wrap">
                            {tabs.map((t) => (
                                <Button
                                    key={t.value}
                                    variant={clubTab === t.value ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setClubTab(t.value)}
                                >
                                    {t.label}
                                    {t.count != null ? ` (${t.count})` : ''}
                                </Button>
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => refetch()}>
                                <RefreshCw className="w-4 h-4" />
                            </Button>
                            <Button size="sm" onClick={() => setCreateOpen(true)}>
                                <Plus className="w-4 h-4 mr-1" />
                                Alta directa
                            </Button>
                        </div>
                    </div>
                    <div className="relative max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            className="pl-9"
                            placeholder="Buscar por nombre o usuario…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    {isLoading ? (
                        <p className="text-muted-foreground">Cargando…</p>
                    ) : (
                        <div className="space-y-2">
                            {rows.map((r) => (
                                <div
                                    key={r.id}
                                    className="flex flex-wrap items-start justify-between gap-2 p-4 rounded-lg border border-border bg-card hover:border-primary/30 transition-colors"
                                >
                                    <Link to={`/clubs/${r.id}`} className="min-w-0 flex-1 group">
                                        <p className="font-semibold group-hover:text-primary transition-colors">
                                            [{r.initials}] {r.name}{' '}
                                            {r.verified ? (
                                                <span className="text-xs text-violet-500">✓</span>
                                            ) : null}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            @{r.creator_username} · {r.visibility} · {r.status}
                                        </p>
                                        <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{r.description}</p>
                                    </Link>
                                    <div className="flex flex-wrap gap-1 shrink-0 items-center">
                                        {r.status === 'pending' ? (
                                            <>
                                                <Button
                                                    size="sm"
                                                    variant="default"
                                                    onClick={() => setStatus.mutate({ id: r.id, status: 'approved' })}
                                                >
                                                    <Check className="w-4 h-4" />
                                                </Button>
                                                <Button size="sm" variant="outline" onClick={() => setRejectId(r.id)}>
                                                    <XCircle className="w-4 h-4" />
                                                </Button>
                                            </>
                                        ) : null}
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="text-destructive"
                                            onClick={() => setDeleteClubId(r.id)}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                        <Button size="sm" variant="outline" asChild>
                                            <Link to={`/clubs/${r.id}`} title="Ver detalle">
                                                <ChevronRight className="w-4 h-4" />
                                            </Link>
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="reports" className="space-y-4 mt-4">
                    <div className="flex gap-2">
                        <Button
                            variant={reportTab === 'open' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setReportTab('open')}
                        >
                            Abiertas
                        </Button>
                        <Button
                            variant={reportTab === 'all' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setReportTab('all')}
                        >
                            Todas
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => refetchReports()}>
                            <RefreshCw className="w-4 h-4" />
                        </Button>
                    </div>
                    <div className="space-y-2">
                        {reports.map((rep) => (
                            <div
                                key={rep.report_id}
                                className="p-4 rounded-lg border border-border flex flex-wrap justify-between gap-2"
                            >
                                <div>
                                    <p className="font-medium flex items-center gap-2">
                                        <Flag className="w-4 h-4" />
                                        <Link to={`/clubs/${rep.club_id}`} className="hover:text-primary hover:underline">
                                            {rep.club_name}
                                        </Link>
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        Por @{rep.reporter_username} · {rep.reason}
                                    </p>
                                    {rep.details ? (
                                        <p className="text-sm mt-1">{rep.details}</p>
                                    ) : null}
                                    <p className="text-xs text-muted-foreground mt-1">Estado: {rep.status}</p>
                                </div>
                                {rep.status === 'open' ? (
                                    <div className="flex gap-1">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() =>
                                                reportSet.mutate({
                                                    reportId: rep.report_id,
                                                    status: 'dismissed',
                                                    notes: 'Revisado',
                                                })
                                            }
                                        >
                                            Descartar
                                        </Button>
                                        <Button
                                            size="sm"
                                            onClick={() =>
                                                reportSet.mutate({
                                                    reportId: rep.report_id,
                                                    status: 'resolved',
                                                    notes: 'Tomadas medidas',
                                                })
                                            }
                                        >
                                            Resolver
                                        </Button>
                                    </div>
                                ) : null}
                            </div>
                        ))}
                    </div>
                </TabsContent>
            </Tabs>

            <Dialog open={Boolean(rejectId)} onOpenChange={(o) => !o && setRejectId(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Rechazar club</DialogTitle>
                    </DialogHeader>
                    <Textarea
                        placeholder="Motivo"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                    />
                    <DialogFooter>
                        <Button
                            onClick={() => {
                                if (!rejectId) return;
                                setStatus.mutate(
                                    { id: rejectId, status: 'rejected', rejectionReason: rejectReason || '—' },
                                    { onSuccess: () => { setRejectId(null); setRejectReason(''); } }
                                );
                            }}
                        >
                            Confirmar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Alta directa (aprobado)</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>UUID del usuario creador</Label>
                            <Input
                                value={create.creator_user_id}
                                onChange={(e) => setCreate((c) => ({ ...c, creator_user_id: e.target.value }))}
                                placeholder="user_id"
                            />
                        </div>
                        <div>
                            <Label>Nombre del club</Label>
                            <Input value={create.name} onChange={(e) => setCreate((c) => ({ ...c, name: e.target.value }))} />
                        </div>
                        <div>
                            <Label>Siglas (1–5)</Label>
                            <Input
                                maxLength={5}
                                value={create.initials}
                                onChange={(e) =>
                                    setCreate((c) => ({ ...c, initials: e.target.value.toUpperCase().slice(0, 5) }))
                                }
                            />
                        </div>
                        <div>
                            <Label>Descripción</Label>
                            <Textarea
                                value={create.description}
                                onChange={(e) => setCreate((c) => ({ ...c, description: e.target.value }))}
                                rows={2}
                            />
                        </div>
                        <div>
                            <Label>Enlace</Label>
                            <Input
                                value={create.external_url}
                                onChange={(e) => setCreate((c) => ({ ...c, external_url: e.target.value }))}
                            />
                        </div>
                        <div>
                            <Label>País (ISO2)</Label>
                            <Input
                                value={create.country_code}
                                onChange={(e) => setCreate((c) => ({ ...c, country_code: e.target.value.toUpperCase() }))}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button onClick={handleCreate} disabled={createMut.isPending}>
                            Crear
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AdminConfirmDialog
                open={!!deleteClubId}
                variant="danger"
                title="Eliminar club"
                description={<p>¿Eliminar este club? Esta acción no se puede deshacer.</p>}
                confirmLabel="Eliminar"
                onCancel={() => setDeleteClubId(null)}
                onConfirm={() => {
                    if (deleteClubId) {
                        del.mutate(deleteClubId);
                        setDeleteClubId(null);
                    }
                }}
                confirmDisabled={del.isPending}
            />
        </div>
    );
}
